import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Local storage for session data
const LOCAL_KEYS = ["sovha_check_ins", "sovha_journals", "sovha_wins", "sovha_favorites", "sovha_helped_tools", "sovha_chats"];

function getLocalData() {
  const data = {};
  data.check_ins = JSON.parse(localStorage.getItem("sovha_check_ins") || "[]");
  data.journals = JSON.parse(localStorage.getItem("sovha_journals") || "[]");
  data.wins = JSON.parse(localStorage.getItem("sovha_wins") || "[]");
  data.favorites = JSON.parse(localStorage.getItem("sovha_favorites") || "[]");
  data.helped_tools = JSON.parse(localStorage.getItem("sovha_helped_tools") || "[]");
  data.chats = JSON.parse(localStorage.getItem("sovha_chats") || "[]");
  return data;
}

function hasLocalData() {
  return LOCAL_KEYS.some(k => {
    const d = JSON.parse(localStorage.getItem(k) || "[]");
    return d.length > 0;
  });
}

function clearLocalData() {
  LOCAL_KEYS.forEach(k => localStorage.removeItem(k));
}

export function addLocalItem(key, item) {
  const items = JSON.parse(localStorage.getItem(key) || "[]");
  items.push(item);
  localStorage.setItem(key, JSON.stringify(items));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalContext, setAuthModalContext] = useState("");
  const pendingRetryRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const syncLocalData = useCallback(async () => {
    if (!hasLocalData()) return;
    try {
      await axios.post(`${API}/sync`, getLocalData(), { withCredentials: true });
      clearLocalData();
    } catch (e) {
      console.error("Sync failed:", e);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      setShowAuthModal(false);
      await syncLocalData();
      // Retry pending action
      if (pendingRetryRef.current) {
        const fn = pendingRetryRef.current;
        pendingRetryRef.current = null;
        setTimeout(fn, 100);
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
      setUser(data);
      setShowAuthModal(false);
      await syncLocalData();
      if (pendingRetryRef.current) {
        const fn = pendingRetryRef.current;
        pendingRetryRef.current = null;
        setTimeout(fn, 100);
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    setUser(false);
  };

  // Show auth prompt for save actions
  const promptAuth = (contextMessage, retryFn) => {
    setAuthModalContext(contextMessage || "Save this?");
    pendingRetryRef.current = retryFn || null;
    setShowAuthModal(true);
  };

  const dismissAuthModal = () => {
    setShowAuthModal(false);
    pendingRetryRef.current = null;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      showAuthModal, authModalContext, promptAuth, dismissAuthModal,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
